import { t } from "i18next";
import _, { isUndefined } from "lodash";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { useFetch } from "../../../../hooks/useFetch";
import { calculateSummaryUsage } from "../../../../lib/calculations";
import { useAppStore } from "../../../../stores/RootStore";
import { UsageRow } from "../../../../stores/TabsStore";
import { Equipment, EquipmentChange, Person, SelectOption, UsageDataset } from "../../../../types/types";
import { Button } from "../../../../ui/Button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger } from "../../../../ui/DropdownMenu/DropdownMenu";
import { MonthPicker } from "../../../../ui/MonthPicker/MonthPicker";
import { ScrollArea } from "../../../../ui/ScrollArea/ScrollArea";
import Spinner from "../../../../ui/Spinner/Spinner";
import { EquipmentDay } from "./blocks/EquipmentDay";
import { EquipmentLoaded } from "./blocks/EquipmentLoaded";
import { LoadBarChart } from "./blocks/LoadBarChart";


interface DateTimeRange {
  from: DateTime,
  to: DateTime
}

interface ParameterViolation {
  id: string,
  at: DateTime,
  type: string,
  description: string,
  equipment: Equipment
}

export function MonthReport({equipment}:{equipment: Equipment}) {
    const {shifts, persons} = useAppStore()
    const shiftsList = shifts.map(s => ({label: s.name, value: s.id})) as SelectOption[]
    const [selectedDateRange, setSelectedDateRange] = useState<DateTimeRange>({
      from: DateTime.local().startOf('month'),
      to: DateTime.local().endOf('month')
    })
    const [currentShift, setCurrentShift] = useState<SelectOption>(shiftsList.find(s => s.value === "full")!)
    const [loadingMonth, setLoadingMonth] = useState<boolean>(true)
    const [dataIsLoading, setDataIsLoading] = useState<boolean>(true)
    const [monthParams, setMonthParams] = useState(new URLSearchParams({}));
    const [violationParams, setViolationParams] = useState(new URLSearchParams({}));
    const [monthData, setMonthData] = useState<UsageDataset[] | null>(null)
    const [violations, setViolations] = useState<ParameterViolation[]>([])
    const [{ response: monthResponse, isLoading: monthLoading, error: monthError }, fetchMonthData] =
    useFetch(`/directories/ng_equipment_item_events/usage_dataset?${monthParams.toString()}`);
    const [{ response: monthViolationResponse, isLoading: monthViolationLoading, error: monthViolationError }, fetchMonthViolationData] =
    useFetch(`/directories/equipment_items/changes/filter?${violationParams.toString()}`);
  
    useEffect(() => {
      setDataIsLoading(true)
      setMonthData(null)
      setLoadingMonth(true)
      setParamsForMonth()
      setParamsForViolations()
      fetchMonthData()
      fetchMonthViolationData()
    }, [])
  
    useEffect(() => {
      if(!selectedDateRange || !currentShift) { return }
      setDataIsLoading(true)
      setMonthData(null)
      setLoadingMonth(true)
      setParamsForMonth()
      setParamsForViolations()
      fetchMonthData()
      fetchMonthViolationData()
    },[currentShift]) 
  
    useEffect(() => {
      if(!selectedDateRange || !currentShift) { return }

      setDataIsLoading(true)
      setMonthData(null)
      setLoadingMonth(true)
      setParamsForMonth()
      setParamsForViolations()
      fetchMonthData()
      fetchMonthViolationData()
    }, [selectedDateRange])

    function setParamsForViolations() {
      let newViolationParams = new URLSearchParams({});
      newViolationParams.append("equipment_item_ids[]", equipment.id)
      newViolationParams.append("types[]", "Sites::EquipmentItemChanges::TriggerValueLessThan");
      newViolationParams.append("types[]", "Sites::EquipmentItemChanges::TriggerValueMoreThan");
      newViolationParams.append("from", selectedDateRange.from.startOf('month').toSQL()!);
      newViolationParams.append("to", selectedDateRange.to.endOf('month').toSQL()!);
      setViolationParams(newViolationParams)
    }
  
    function setParamsForMonth() {
      let newMonthParams = new URLSearchParams({});
      let responseShift = shifts.find(s => s.id == currentShift!.value)!
  
      let endOfShiftResponse = selectedDateRange.from.set({hour: responseShift.end_at.hour, minute: responseShift.end_at.minute, second: responseShift.end_at.second})
      
      newMonthParams.append("equipment_item_ids[]", equipment.id)
      newMonthParams.append("from", selectedDateRange.from.startOf('month').toSQL()!);
      newMonthParams.append("to", selectedDateRange.to.endOf('month').toSQL()!);
      newMonthParams.append("day_time_from", responseShift?.start_at.toFormat("HH:mm:ss")!);
      if(endOfShiftResponse > DateTime.local()) {
        newMonthParams.append("day_time_to", responseShift?.end_at.toFormat("HH:mm:ss")!);
      } else {
        newMonthParams.append("day_time_to", responseShift?.end_at.toFormat("HH:mm:ss")!);
      }
      setMonthParams(newMonthParams)
    }
  
    useEffect(() => {
      if(!monthResponse) { return }
      setMonthData(_.orderBy(monthResponse.data, "at") as unknown as UsageDataset[])
      setLoadingMonth(false)
      setDataIsLoading(false)
    }, [monthResponse])

    useEffect(() => {
      if(!monthViolationResponse) { return }
      let response = (monthViolationResponse.data as unknown as EquipmentChange[]).map((v: EquipmentChange) => ({
        id: v.id, 
        equipment: equipment, 
        at: DateTime.fromISO(v.attributes.at), 
        type: v.type, 
        description: v.attributes.description
      })) as ParameterViolation[]
      setViolations(response)
    }, [monthViolationResponse])

    const data = useMemo(() => {
      return calculateSummaryUsage(monthData!, shifts.find(s => s.id === currentShift.value)!, equipment, persons!)
    }, [monthData])
  
    function handleChangeDate(value: DateTimeRange) {
      setDateWithChecking(value!)
    }
  
    function handleChangeCurrentShift(value: string) {
      setCurrentShift(shiftsList.find(s => s.value == value)!)
    }

    const setDateWithChecking = (value: DateTimeRange | any) => {
      if (isUndefined(value)) {
        setSelectedDateRange({
          from: selectedDateRange.from.startOf('month'),
          to: selectedDateRange.from.endOf('month')
        });
        return;
      }
      if (isUndefined(value.from))
        setSelectedDateRange({
        from: DateTime.local().startOf("month"),
        to: DateTime.local().endOf('month'),
        });
      if (isUndefined(value.to))
        setSelectedDateRange({
          from: value.startOf("month"),
          to: value.endOf('month'),
        });
      else setSelectedDateRange(value);
    };
  
    return (
      <div className="flex flex-col w-full h-full overflow-hidden">
        <div className="w-full">
          {selectedDateRange && currentShift && <div className={`flex flex-none justify-between py-2 px-6 border-b ${dataIsLoading ? "pointer-events-none" : ""}`}>
            <div className="flex items-center">
              <span className="text-sm">{t('report_by_period')}</span>
              <MonthPicker 
                selected={selectedDateRange}
                onSelect={handleChangeDate}
                variant={"link"}
                algin={"start"}
                classname="text-primary"
              />
              <span className="text-sm">{t('by_shift')}</span>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant={"link"} size={"datepicker"} className="lowercase">{currentShift?.label}</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-[200px]">
                  <DropdownMenuLabel>{t("shifts")}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuRadioGroup value={currentShift?.value} onValueChange={(value) => handleChangeCurrentShift(value)}>
                    {shiftsList.map(shift => (
                      <DropdownMenuRadioItem key={shift.value} value={shift.value}>{shift.label}</DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>

            </div>
            {dataIsLoading && monthData && monthData.length === 0 ? <></> : <CSVLink data={getCSVData(data.data, equipment, persons!)} filename={`${equipment.attributes.inventory_number}_${selectedDateRange.from.monthLong}.csv`} separator={";"}>
              <Button variant="outline" size="datepicker">{t('export')}</Button>
            </CSVLink>}
          </div>}
        </div>
        {loadingMonth ? <div className="flex h-full w-full"><Spinner/></div> : 
          monthData && monthData.length === 0 ? <div className="flex w-full h-full items-center justify-center px-6">{t('no_data_for_this_period')}</div> :
            <ScrollArea>
              <div className="flex flex-col gap-2">
                <div className="px-6">
                  <EquipmentLoaded equipment={equipment} data={data}/>
                </div>
                <div className="px-6 space-y-2">
                  <LoadBarChart data={data}/>
                </div>
                <div className="px-6 h-full">
                  <EquipmentDay data={data} equipment={equipment} violations={violations}/>
                </div>
              </div>
            </ScrollArea>
          }
    </div>
  )

}


function getCSVData(rows: UsageRow[], equipment: Equipment, persons: Person[]) {
  // CSV
  return [
    [
      "ID",
      t("at"),
      t("weekday"),
      t("equipment_name"),
      t("inventory_number"),
      t("shift"),
      t("availability.total"),
      t("availability.total"),
      t("availability.total"),
      t("availability.on"),
      t("availability.on"),
      t("availability.on"),
      t("availability.off"),
      t("availability.off"),
      t("availability.off"),
      t("availability.worked"),
      t("availability.worked"),
      t("availability.worked"),
      t("availability.stopped"),
      t("availability.stopped"),
      t("availability.stopped"),

      t("works.total"),
      t("works.total"),
      t("works.total"),
      t("works.on"),
      t("works.on"),
      t("works.on"),
      t("works.off"),
      t("works.off"),
      t("works.off"),
      t("works.worked"),
      t("works.worked"),
      t("works.worked"),
      t("works.stopped"),
      t("works.stopped"),
      t("works.stopped"),

      t("maintenance.total"),
      t("maintenance.total"),
      t("maintenance.total"),
      t("maintenance.on"),
      t("maintenance.on"),
      t("maintenance.on"),
      t("maintenance.off"),
      t("maintenance.off"),
      t("maintenance.off"),
      t("maintenance.worked"),
      t("maintenance.worked"),
      t("maintenance.worked"),
      t("maintenance.stopped"),
      t("maintenance.stopped"),
      t("maintenance.stopped"),

      t("unavailability.total"),
      t("unavailability.total"),
      t("unavailability.total"),
      t("unavailability.on"),
      t("unavailability.on"),
      t("unavailability.on"),
      t("unavailability.off"),
      t("unavailability.off"),
      t("unavailability.off"),
      t("unavailability.worked"),
      t("unavailability.worked"),
      t("unavailability.worked"),
      t("unavailability.stopped"),
      t("unavailability.stopped"),
      t("unavailability.stopped"),

      t("broken.total"),
      t("broken.total"),
      t("broken.total"),
      t("broken.on"),
      t("broken.on"),
      t("broken.on"),
      t("broken.off"),
      t("broken.off"),
      t("broken.off"),
      t("broken.worked"),
      t("broken.worked"),
      t("broken.worked"),
      t("broken.stopped"),
      t("broken.stopped"),
      t("broken.stopped"),

      t("repair.total"),
      t("repair.total"),
      t("repair.total"),
      t("repair.on"),
      t("repair.on"),
      t("repair.on"),
      t("repair.off"),
      t("repair.off"),
      t("repair.off"),
      t("repair.worked"),
      t("repair.worked"),
      t("repair.worked"),
      t("repair.stopped"),
      t("repair.stopped"),
      t("repair.stopped"),

      t("service.total"),
      t("service.total"),
      t("service.total"),
      t("service.on"),
      t("service.on"),
      t("service.on"),
      t("service.off"),
      t("service.off"),
      t("service.off"),
      t("service.worked"),
      t("service.worked"),
      t("service.worked"),
      t("service.stopped"),
      t("service.stopped"),
      t("service.stopped"),

      t("other.total"),
      t("other.total"),
      t("other.total"),
      t("other.on"),
      t("other.on"),
      t("other.on"),
      t("other.off"),
      t("other.off"),
      t("other.off"),
      t("other.worked"),
      t("other.worked"),
      t("other.worked"),
      t("other.stopped"),
      t("other.stopped"),
      t("other.stopped"),

      t("interactions_count"),
      t("interactions_with")
    ],
    ...rows!.map((row, i) => [
      i,
      row.dateShift.start_day.toSQLDate(),
      row.dateShift.start_day.weekdayLong,
      equipment.attributes.name,
      equipment.attributes.inventory_number,
      row.dateShift.label,
      row.availability.total.time,
      row.availability.total.seconds,
      row.availability.total.percent * 100,
      row.availability.on.time,
      row.availability.on.seconds,
      row.availability.on.percent * 100,
      row.availability.off.time,
      row.availability.off.seconds,
      row.availability.off.percent * 100,
      row.availability.worked.time,
      row.availability.worked.seconds,
      row.availability.worked.percent * 100,
      row.availability.stopped.time,
      row.availability.stopped.seconds,
      row.availability.stopped.percent * 100,

      row.works.total.time,
      row.works.total.seconds,
      row.works.total.percent * 100,
      row.works.on.time,
      row.works.on.seconds,
      row.works.on.percent * 100,
      row.works.off.time,
      row.works.off.seconds,
      row.works.off.percent * 100,
      row.works.worked.time,
      row.works.worked.seconds,
      row.works.worked.percent * 100,
      row.works.stopped.time,
      row.works.stopped.seconds,
      row.works.stopped.percent * 100,

      row.maintenance.total.time,
      row.maintenance.total.seconds,
      row.maintenance.total.percent * 100,
      row.maintenance.on.time,
      row.maintenance.on.seconds,
      row.maintenance.on.percent * 100,
      row.maintenance.off.time,
      row.maintenance.off.seconds,
      row.maintenance.off.percent * 100,
      row.maintenance.worked.time,
      row.maintenance.worked.seconds,
      row.maintenance.worked.percent * 100,
      row.maintenance.stopped.time,
      row.maintenance.stopped.seconds,
      row.maintenance.stopped.percent * 100,

      row.unavailability.total.time,
      row.unavailability.total.seconds,
      row.unavailability.total.percent * 100,
      row.unavailability.on.time,
      row.unavailability.on.seconds,
      row.unavailability.on.percent * 100,
      row.unavailability.off.time,
      row.unavailability.off.seconds,
      row.unavailability.off.percent * 100,
      row.unavailability.worked.time,
      row.unavailability.worked.seconds,
      row.unavailability.worked.percent * 100,
      row.unavailability.stopped.time,
      row.unavailability.stopped.seconds,
      row.unavailability.stopped.percent * 100,

      row.broken.total.time,
      row.broken.total.seconds,
      row.broken.total.percent * 100,
      row.broken.on.time,
      row.broken.on.seconds,
      row.broken.on.percent * 100,
      row.broken.off.time,
      row.broken.off.seconds,
      row.broken.off.percent * 100,
      row.broken.worked.time,
      row.broken.worked.seconds,
      row.broken.worked.percent * 100,
      row.broken.stopped.time,
      row.broken.stopped.seconds,
      row.broken.stopped.percent * 100,

      row.repair.total.time,
      row.repair.total.seconds,
      row.repair.total.percent * 100,
      row.repair.on.time,
      row.repair.on.seconds,
      row.repair.on.percent * 100,
      row.repair.off.time,
      row.repair.off.seconds,
      row.repair.off.percent * 100,
      row.repair.worked.time,
      row.repair.worked.seconds,
      row.repair.worked.percent * 100,
      row.repair.stopped.time,
      row.repair.stopped.seconds,
      row.repair.stopped.percent * 100,

      row.service.total.time,
      row.service.total.seconds,
      row.service.total.percent * 100,
      row.service.on.time,
      row.service.on.seconds,
      row.service.on.percent * 100,
      row.service.off.time,
      row.service.off.seconds,
      row.service.off.percent * 100,
      row.service.worked.time,
      row.service.worked.seconds,
      row.service.worked.percent * 100,
      row.service.stopped.time,
      row.service.stopped.seconds,
      row.service.stopped.percent * 100,

      row.other.total.time,
      row.other.total.seconds,
      row.other.total.percent * 100,
      row.other.on.time,
      row.other.on.seconds,
      row.other.on.percent * 100,
      row.other.off.time,
      row.other.off.seconds,
      row.other.off.percent * 100,
      row.other.worked.time,
      row.other.worked.seconds,
      row.other.worked.percent * 100,
      row.other.stopped.time,
      row.other.stopped.seconds,
      row.other.stopped.percent * 100,

      row.interactionIds.filter(i => i > 0).length,
      row.interactionIds.filter(i => i > 0).map(i => {
        let person = persons.find(p => Number(p.id) === i)
        return `${person?.attributes.last_name} ${person?.attributes.first_name} ${person?.attributes.middle_name}`
      }).join(", ")
    ]),
  ];
}